import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container
} from '@mui/material';
import ConstructionIcon from '@mui/icons-material/Construction';
import toast from 'react-hot-toast';
import api from '../utils/api';
import PasswordRequirements from '../components/PasswordRequirements';
import { getPasswordRequirementError, isPasswordComplex } from '../utils/passwordComplexity';

export default function ResetPassword() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      setError('Invalid or missing reset token');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const requirementError = getPasswordRequirementError(newPassword, confirmPassword);
    if (requirementError) {
      setError(requirementError);
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', {
        token,
        newPassword,
        confirmPassword
      });

      setSuccess(true);
      toast.success('Password reset successfully!');

      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      const msg =
        (typeof err.response?.data?.message === 'string' && err.response.data.message) ||
        (typeof err.response?.data?.error === 'string' && err.response.data.error) ||
        err.message ||
        'Failed to reset password';
      if (/invalid or expired|invalid or missing/i.test(msg)) {
        setTokenValid(false);
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="sm" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 4 }}>
        <Box sx={{ width: '100%', maxWidth: 450 }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 6, justifyContent: 'center' }}>
            <ConstructionIcon sx={{ fontSize: 50, color: 'primary.main', mr: 2 }} />
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '2.5rem' }}>
              Galldris Core
            </Typography>
          </Box>

          {/*Header */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1.5rem', mb: 1 }}>
              Reset Your Password
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Enter your new password below
            </Typography>
          </Box>

          {!tokenValid ? (
            <>
              <Alert severity="error" sx={{ mb: 3 }}>
                Your password reset link has expired or is invalid. Please request a new one.
              </Alert>
              <Button
                fullWidth
                variant="contained"
                component={Link}
                to="/login"
                sx={{ py: 1.5, fontSize: '1rem', fontWeight: 600 }}
              >
                Back to Login
              </Button>
            </>
          ) : success ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'success.main' }}>
                ✓ Password Reset Successful
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Your password has been updated. You'll be redirected to the login page shortly.
              </Typography>
              <CircularProgress />
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ position: 'relative', mb: 2 }}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="Enter a new password"
                  variant="outlined"
                />
              </Box>

              <TextField
                fullWidth
                label="Confirm Password"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                variant="outlined"
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <input
                  type="checkbox"
                  id="showPassword"
                  checked={showPassword}
                  onChange={(e) => setShowPassword(e.target.checked)}
                  style={{ cursor: 'pointer', marginRight: 8 }}
                  disabled={loading}
                />
                <label htmlFor="showPassword" style={{ cursor: 'pointer', fontSize: '0.875rem' }}>
                  Show passwords
                </label>
              </Box>

              <PasswordRequirements
                password={newPassword}
                confirmPassword={confirmPassword}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !isPasswordComplex(newPassword) || newPassword !== confirmPassword}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  bgcolor: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  }
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Remember your password?{' '}
                  <Link
                    to="/login"
                    style={{
                      color: 'primary.main',
                      fontWeight: 600,
                      textDecoration: 'none'
                    }}
                  >
                    Back to Login
                  </Link>
                </Typography>
              </Box>
            </form>
          )}

          {/* Footer */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 6, textAlign: 'center' }}>
            © 2026 Galldris Core. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
